import { dollarsInputToCents } from './money';
import type { AddressForm } from './types';

/**
 * The create/update body — the same shape either way, differing only in which
 * mutation receives it.
 *
 * Blank optional text becomes null rather than "", and warehouses with no rate
 * typed in are dropped entirely: sending them as zero would look like a
 * configured free delivery.
 */
export const buildAddressPayload = (form: AddressForm) => {
  const carrierRates = Object.entries(form.carrierRates ?? {})
    .filter(([, value]) => String(value ?? '').trim() !== '')
    .map(([warehouseId, value]) => ({
      warehouseId,
      carrierRateCents: dollarsInputToCents(value),
    }));

  return {
    label: form.label.trim() || null,
    fullName: form.fullName.trim(),
    phone: form.phone.trim(),
    line1: form.line1.trim(),
    line2: form.line2.trim() || null,
    city: form.city.trim(),
    state: form.state.trim(),
    postalCode: form.postalCode.trim(),
    country: form.country.trim(),
    carrierRates,
    orgLoadOptionIds: form.orgLoadOptionIds,
    defaultFulfillmentMethodId: form.defaultFulfillmentMethodId,
    isPrimary: Boolean(form.isPrimary),
    isHeadquarters: Boolean(form.isHeadquarters),
    operatingHours: form.operatingHours,
    deliveryNotes: form.deliveryNotes.trim() || null,
    requiresPoNumber: Boolean(form.requiresPoNumber),
  };
};

/**
 * Server error codes turned into something a dispatcher can act on. Anything
 * unrecognised falls through to the raw code, which is still better than a
 * generic failure with no clue what went wrong.
 */
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  WAREHOUSE_NOT_AVAILABLE_FOR_CUSTOMER:
    'One or more warehouses are no longer available for this customer. Refresh the form and try again.',
  INVALID_CARRIER_RATE: 'One or more carrier rates are invalid.',
  LOAD_OPTION_REQUIRED: 'Select at least one fulfilment method.',
  INVALID_LOAD_OPTION:
    'One or more selected fulfilment methods are invalid, inactive, or no longer available.',
  DEFAULT_LOAD_OPTION_REQUIRED: 'Select a default fulfilment method.',
  DEFAULT_LOAD_OPTION_NOT_ASSIGNED:
    'The default fulfilment method must be one of the methods assigned to this location.',
};

export const saveErrorMessage = (code: unknown): string =>
  SAVE_ERROR_MESSAGES[String(code)] ?? (code ? String(code) : 'Save failed.');

const DELETE_ERROR_MESSAGES: Record<string, string> = {
  ADDRESS_IN_USE: 'This address cannot be removed because it is used by an order.',
};

export const deleteErrorMessage = (code: unknown): string =>
  DELETE_ERROR_MESSAGES[String(code)] ?? (code ? String(code) : 'Delete failed.');
